import React, { useEffect, useMemo, useRef } from 'react'
import { subscribe } from 'react-contextual'
import classNames from 'classnames'
import { format } from 'date-fns'
import { DateRangePicker } from 'components'
import { $TSFixMe } from 'types/ts-migrate'
import ViewStatementProvider from '../ViewStatementProvider'
import { DATE_FORMAT, resolvePeriodKey, STATEMENT_PERIODS } from '../statementPeriods'
import styles from './ViewStatementControls.module.scss'

interface ViewStatementControlsProps extends $TSFixMe {
  startDate: string
  endDate: string
}

const ViewStatementControls = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onParamChange,
}: ViewStatementControlsProps): React.ReactElement => {
  useEffect(() => {
    onParamChange(startDate, endDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const selectedPeriod = useMemo(() => resolvePeriodKey(startDate, endDate), [startDate, endDate])

  const applyPeriod = (range: { startDate: string; endDate: string }) => {
    setStartDate(range.startDate)
    setEndDate(range.endDate)
  }

  const dateRangeRef = useRef<HTMLDivElement>(null)
  const focusStartDate = () => dateRangeRef.current?.querySelector('input')?.focus()

  return (
    <div className={styles.root}>
      <div className={styles.period}>
        <div className={styles.presets} role="group" aria-label="Statement period">
          {STATEMENT_PERIODS.map(period => (
            <button
              key={period.key}
              type="button"
              className={classNames(styles.preset, { [styles['preset--selected']]: selectedPeriod === period.key })}
              aria-pressed={selectedPeriod === period.key}
              onClick={() => applyPeriod(period.range(new Date()))}
            >
              {period.label}
            </button>
          ))}
          <button
            type="button"
            className={classNames(styles.preset, { [styles['preset--selected']]: selectedPeriod === 'custom' })}
            aria-pressed={selectedPeriod === 'custom'}
            onClick={focusStartDate}
          >
            Custom
          </button>
        </div>
      </div>

      <div className={styles['date-range-picker']} ref={dateRangeRef}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(date: $TSFixMe, isStartDate: boolean) =>
            isStartDate ? setStartDate(format(date, DATE_FORMAT)) : setEndDate(format(date, DATE_FORMAT))
          }
          inline={false}
        />
      </div>
    </div>
  )
}

export default subscribe(
  [ViewStatementProvider],
  ({ startDate, setStartDate, endDate, setEndDate, onParamChange }: any) => ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    onParamChange,
  }),
)(ViewStatementControls)
